import { useMemo } from 'react';
import { useBundleCatalog } from '@features/subscriptions';
import type { BundleWithStatus } from '@features/subscriptions/types';

interface JotMeetAvailability {
  /**
   * Whether the JotMeet addon is offered to this user (i.e. their bundle
   * catalog contains an add-on granting the `telehealth` capability).
   *
   * Backend gates the JotMeet rollout via the `feature_telehealth` Statsig
   * gate by deciding whether to include the addon in the catalog response.
   * The frontend uses catalog presence as the single source of truth.
   *
   * False while the catalog is loading to prevent flash.
   */
  isAvailable: boolean;
  /** The JotMeet addon bundle, when available — used for pricing in the marketing CTA. */
  addon: BundleWithStatus | null;
  /** Whether the catalog query is still in-flight. */
  isLoading: boolean;
}

/**
 * Derives whether the JotMeet addon is offered to the current user from the
 * bundle catalog. Used to drive nav-item visibility and to power the
 * marketing view's price line.
 */
export function useJotMeetAvailability(): JotMeetAvailability {
  const { addOns, isLoading } = useBundleCatalog();

  const addon = useMemo(
    () =>
      addOns.find(b => b.grants?.some(g => g.capability_name === 'telehealth')) ?? null,
    [addOns],
  );

  return {
    isAvailable: !isLoading && addon != null,
    addon,
    isLoading,
  };
}
